/**
 * FacebookPixelEditor - Configuration editor for Facebook Pixel
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  Link,
  Divider,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  FormHelperText,
  Alert,
} from '@mui/material';
import type {
  AnalyticsIntegration,
  FacebookPixelConfig,
  FacebookPixelEventMode,
  PlatformConfig,
} from '../../types/analytics.types';
import { ANALYTICS_SETTINGS_TEST_IDS } from '@modules/analytics-settings/testIds';
import { hasCustomScriptMetaMapper } from './fbPixelConflict';

interface FacebookPixelEditorProps {
  config: PlatformConfig;
  onChange: (config: PlatformConfig, name?: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  showSaveButton?: boolean;
  onSave?: (config: PlatformConfig, name?: string) => void;
  name?: string;
  /** Sibling integrations in the same project (D5 conflict check against Custom Script mappers). */
  siblingIntegrations?: AnalyticsIntegration[];
}

/**
 * Normalizes `eventMode` to the two UI-offered values, so a legacy config (stored `undefined`,
 * runtime-treated as `raw`) or an explicit `raw` is always displayed AND saved as `standard`
 * once it passes through this editor — even if the user never touches the radio group.
 */
const normalizeEventMode = (config: FacebookPixelConfig): FacebookPixelConfig => ({
  ...config,
  eventMode: config.eventMode === 'all' ? 'all' : 'standard',
});

export const FacebookPixelEditor: React.FC<FacebookPixelEditorProps> = ({
  config,
  onChange,
  disabled = false,
  readOnly = false,
  showSaveButton = false,
  onSave,
  name,
  siblingIntegrations,
}) => {
  const fbConfig = config as FacebookPixelConfig;
  const [localConfig, setLocalConfig] = useState<FacebookPixelConfig>(() => normalizeEventMode(fbConfig));
  const [localName, setLocalName] = useState(name || '');

  useEffect(() => {
    setLocalConfig(normalizeEventMode(fbConfig));
    setLocalName(name || '');
  }, [fbConfig, name]);

  const handleChange = useCallback(
    (field: keyof FacebookPixelConfig, value: unknown) => {
      const newConfig = { ...localConfig, [field]: value };
      setLocalConfig(newConfig);
      if (!showSaveButton) {
        onChange(newConfig, localName);
      }
    },
    [localConfig, localName, onChange, showSaveButton]
  );

  const handleSave = useCallback(() => {
    if (onSave) {
      onSave(localConfig, localName);
    }
  }, [onSave, localConfig, localName]);

  // Display rule (D3): undefined/raw show as "Standard events only". `localConfig.eventMode`
  // is already normalized above, but the rule is kept explicit here per spec.
  const eventModeValue: Exclude<FacebookPixelEventMode, 'raw'> =
    localConfig.eventMode === 'all' ? 'all' : 'standard';

  const hasConflict = useMemo(
    () => hasCustomScriptMetaMapper(siblingIntegrations),
    [siblingIntegrations]
  );

  return (
    <Box>
      <TextField
        label="Pixel ID"
        value={localConfig.pixelId || ''}
        onChange={(e) => handleChange('pixelId', e.target.value)}
        disabled={disabled || readOnly}
        fullWidth
        required
        sx={{ mb: 2 }}
        placeholder="123456789012345"
        helperText={
          <>
            Find your Pixel ID in{' '}
            <Link
              href="https://business.facebook.com/events_manager2/list/pixel"
              target="_blank"
              rel="noopener"
            >
              Meta Events Manager
            </Link>
          </>
        }
        slotProps={{ htmlInput: { 'data-testid': ANALYTICS_SETTINGS_TEST_IDS.editorField('facebook_pixel', 'pixelId') } }}
      />

      <FormControlLabel
        control={
          <Switch
            checked={localConfig.autoConfig ?? true}
            onChange={(e) => handleChange('autoConfig', e.target.checked)}
            disabled={disabled || readOnly}
          />
        }
        label="Auto-config (automatic button click and page metadata tracking)"
      />

      <Divider sx={{ my: 3 }} />

      <FormControl component="fieldset" sx={{ width: '100%' }} disabled={disabled || readOnly}>
        <FormLabel component="legend" sx={{ typography: 'caption', mb: 1 }}>
          Events sent to Meta
        </FormLabel>
        <RadioGroup
          value={eventModeValue}
          onChange={(e) => handleChange('eventMode', e.target.value as FacebookPixelEventMode)}
          data-testid={ANALYTICS_SETTINGS_TEST_IDS.fbPixelEventMode}
        >
          <FormControlLabel
            value="standard"
            control={
              <Radio
                slotProps={{
                  input: {
                    'data-testid': ANALYTICS_SETTINGS_TEST_IDS.fbPixelEventModeOption('standard'),
                  } as React.InputHTMLAttributes<HTMLInputElement>,
                }}
              />
            }
            label="Standard events only (recommended)"
          />
          <FormHelperText sx={{ ml: 4, mt: -0.5, mb: 1.5 }}>
            ViewContent when the funnel opens, CompleteRegistration when an email is entered,
            InitiateCheckout when a paywall is shown, AddPaymentInfo when the purchase button is
            pressed. Best for ad optimization.
          </FormHelperText>

          <FormControlLabel
            value="all"
            control={
              <Radio
                slotProps={{
                  input: {
                    'data-testid': ANALYTICS_SETTINGS_TEST_IDS.fbPixelEventModeOption('all'),
                  } as React.InputHTMLAttributes<HTMLInputElement>,
                }}
              />
            }
            label="Standard events + all funnel events"
          />
          <FormHelperText sx={{ ml: 4, mt: -0.5 }}>
            Adds every Segmently funnel event (StartOnboarding, ScreenDidAppear, …) as a Meta
            custom event. Use if you build custom audiences on individual screens.
          </FormHelperText>
        </RadioGroup>
      </FormControl>

      {hasConflict && (
        <Alert
          severity="warning"
          sx={{ mt: 2 }}
          data-testid={ANALYTICS_SETTINGS_TEST_IDS.fbPixelConflictWarning}
        >
          A Custom Script integration already sends Meta standard events. Keep only one of the
          two, or Meta will count each event twice.
        </Alert>
      )}

      {showSaveButton && (
        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={disabled || !localConfig.pixelId}
            data-testid={ANALYTICS_SETTINGS_TEST_IDS.editorSave('facebook_pixel')}
          >
            Save
          </Button>
        </Box>
      )}
    </Box>
  );
};
